import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo, useTeamAuditData } from 'app/frontend/base/config/queries'
import {
  TrinketChoice,
  updateTrinketPriority,
} from 'app/frontend/base/api/character'
import { displayErrorToast, wowheadUrl } from 'app/frontend/base/helpers'
import { LootContext } from '../context'
import { seasonWishlistMetadata, withSeasonWishlistMetadata } from '../helpers'
import Season from 'app/frontend/types/custom/Season'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Select from 'app/frontend/base/components/Select'
import SeasonSelect from './SeasonSelect'
import NoMemberWarning from './NoMemberWarning'
import TrinketPrioritiesSummary from './TrinketPrioritiesSummary'
import TrinketChoiceCard, { TRINKET_CLEAR_VALUE } from './TrinketChoiceCard'
import Card from 'app/frontend/base/components/Card'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import { cn } from '@/lib/utils'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import { itemQualityClasses } from 'app/frontend/types/custom/ItemQuality'
import EquippedItem from 'app/frontend/types/custom/EquippedItem'
import {
  detectedTrinketTrack,
  isChoiceObtained,
  isTrinketAutoObtained,
  TrinketMemberRow,
  TrinketPriority,
  TrinketRank,
  trinketRankColumnLabels,
  trinketRanks,
  useTrinketItemLookup,
  TrinketItemInfo,
} from './trinketPriorityHelpers'

const TrinketStatusCell = ({
  row,
  rank,
  canEdit,
  lookup,
  minimumTrack,
  onChange,
}: {
  row: TrinketMemberRow
  rank: TrinketRank
  canEdit: boolean
  lookup: Record<number, TrinketItemInfo>
  minimumTrack: UpgradeTrack
  onChange: (choice: TrinketChoice) => void
}) => {
  const {
    data: { gameVersion },
  } = usePageInfo()
  const choice = row.storedPriority[rank]
  const info = choice ? lookup[choice.item_id] : undefined
  const classId = row.member.characterReference.classId
  const autoObtained = choice
    ? isTrinketAutoObtained(choice.item_id, row.trinketCache, minimumTrack)
    : false
  const effectiveObtained = isChoiceObtained(
    choice ?? null,
    row.trinketCache,
    minimumTrack,
  )

  const chosenElsewhere = trinketRanks
    .filter((other) => other !== rank)
    .map((other) => row.storedPriority[other]?.item_id)
    .filter((id): id is number => !!id)

  const options = useMemo(() => {
    const trinketOptions = Object.values(lookup)
      .filter(
        (item) =>
          item.eligibleClasses.includes(classId) &&
          !chosenElsewhere.includes(item.id),
      )
      .map((item) => ({
        value: item.id.toString(),
        sortLabel: `${item.name} ${item.encounterName}`,
        className: `text-quality-${item.quality}`,
        label: item.name,
        customOptionFn: (isSelected: boolean) => (
          <div className='flex items-center gap-2 w-full min-w-0'>
            <a
              href={wowheadUrl(gameVersion, undefined, item.id)}
              target='_blank'
              rel='noreferrer'
              onClick={(event) => event.stopPropagation()}
              className='shrink-0'
            >
              <ItemIcon id={item.id} className='w-6 h-6' />
            </a>
            <div className='grow min-w-0'>
              <div className={cn('truncate', `text-quality-${item.quality}`)}>
                {item.name}
              </div>
              <div className='text-xs text-muted-foreground truncate'>
                {item.encounterName}
              </div>
            </div>
            <Check
              size={16}
              className={cn(
                'ml-auto min-w-4',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
        ),
      }))

    if (!choice) return trinketOptions

    return [
      {
        value: TRINKET_CLEAR_VALUE,
        sortLabel: '!',
        label: 'No trinket',
        className: 'text-muted-foreground',
        customOptionFn: (isSelected: boolean) => (
          <div className='flex items-center gap-2 w-full min-w-0'>
            <span className='truncate text-muted-foreground'>No trinket</span>
            <Check
              size={16}
              className={cn(
                'ml-auto min-w-4',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
        ),
      },
      ...trinketOptions,
    ]
  }, [lookup, classId, chosenElsewhere.join(','), gameVersion, !!choice])

  const obtainedTooltip =
    autoObtained && choice
      ? `Found equipped, on the ${detectedTrinketTrack(
          choice.item_id,
          row.trinketCache,
        )} track`
      : effectiveObtained
      ? 'Obtained'
      : 'Not obtained'

  return (
    <TrinketChoiceCard
      choice={choice ?? null}
      item={info}
      options={options}
      canEdit={canEdit}
      autoObtained={autoObtained}
      effectiveObtained={effectiveObtained}
      obtainedTooltip={obtainedTooltip}
      onChange={onChange}
    />
  )
}

const TrinketPrioritiesOverview = () => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { selectedSeason, setSelectedSeason } = useContext(LootContext)
  const {
    data: {
      currentExpansion: { seasons },
      user,
      gameVersion,
    },
  } = usePageInfo()
  const { data: auditData } = useTeamAuditData(
    team.settings.trinketPriorityEnabled.value ? team.id : undefined,
  )

  const teamQueryKey = ['team', { path: team.path }]
  const { lookup, isPending: isLookupPending } = useTrinketItemLookup(
    selectedSeason.id,
  )
  const [trinketFilterId, setTrinketFilterId] = useState<number | undefined>()
  const minimumTrack = team.settings.tierPriorityMinimumTrack
    .value as UpgradeTrack

  const canEditCharacter = (character: wowaudit.CharacterReference) => {
    if (accessLevel === 'admin') return true
    if (team.settings.wishlistLocked.value) return false
    return !!user && character.userId === user.id
  }

  const mutation = useMutation({
    mutationFn: ({
      characterId,
      nextPriority,
    }: {
      characterId: number
      nextPriority: TrinketPriority
    }) =>
      updateTrinketPriority(
        team.id,
        characterId,
        selectedSeason.id,
        nextPriority,
      ),
    onMutate: async ({ characterId, nextPriority }) => {
      await queryClient.cancelQueries({ queryKey: teamQueryKey })
      const previousTeam = queryClient.getQueryData<wowaudit.Team>(teamQueryKey)

      queryClient.setQueryData<wowaudit.Team>(teamQueryKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.map((m) =>
                m.characterReference.id === characterId
                  ? {
                      ...m,
                      characterReference: {
                        ...m.characterReference,
                        wishlistMetadata: withSeasonWishlistMetadata(
                          m.characterReference.wishlistMetadata,
                          selectedSeason.id,
                          { trinket_priority: nextPriority },
                        ),
                      },
                    }
                  : m,
              ),
            }
          : old,
      )

      return { previousTeam }
    },
    onError: (error, _variables, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamQueryKey, context.previousTeam)
      }
      displayErrorToast(error, t)
    },
    onSuccess: (newTeam) => {
      queryClient.setQueryData(teamQueryKey, newTeam)
    },
  })

  const mutationPendingRef = useRef(false)
  mutationPendingRef.current = mutation.isPending

  const setRankChoice = useCallback(
    (
      characterId: number,
      storedPriority: TrinketPriority,
      rank: TrinketRank,
      choice: TrinketChoice,
    ) => {
      if (mutationPendingRef.current) return
      mutation.mutate({
        characterId,
        nextPriority: { ...storedPriority, [rank]: choice },
      })
    },
    [mutation.mutate],
  )

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      []
    )
  }, [team.members])

  const rows = useMemo<TrinketMemberRow[]>(() => {
    return visibleMembers.map((member) => {
      const characterId = member.characterReference.id
      const gear = auditData?.[characterId]?.current_gear

      return {
        member,
        storedPriority: seasonWishlistMetadata(
          member.characterReference.wishlistMetadata,
          selectedSeason.id,
        ).trinket_priority,
        trinketCache:
          auditData?.[characterId]?.trinket_cache?.[String(selectedSeason.id)],
        equippedTrinkets: [gear?.trinket_1, gear?.trinket_2],
      }
    })
  }, [visibleMembers, selectedSeason.id, auditData])

  const filteredRows = useMemo(() => {
    if (trinketFilterId === undefined) return rows
    return rows.filter((row) =>
      trinketRanks.some(
        (rank) => row.storedPriority[rank]?.item_id === trinketFilterId,
      ),
    )
  }, [rows, trinketFilterId])

  const filterOptions = useMemo(
    () =>
      Object.values(lookup).map((item) => ({
        value: `${item.id}::${item.name}::${item.encounterName}`,
        sortLabel: item.name,
        label: (
          <div className='flex items-center gap-2 min-w-0'>
            <a
              href={wowheadUrl(gameVersion, undefined, item.id)}
              target='_blank'
              rel='noreferrer'
              onClick={(event) => event.stopPropagation()}
              className='shrink-0'
            >
              <ItemIcon id={item.id} className='w-5 h-5' />
            </a>
            <span className={cn('truncate', `text-quality-${item.quality}`)}>
              {item.name}
            </span>
          </div>
        ),
        customOptionFn: (isSelected: boolean) => (
          <div className='flex items-center gap-2 w-full min-w-0'>
            <a
              href={wowheadUrl(gameVersion, undefined, item.id)}
              target='_blank'
              rel='noreferrer'
              onClick={(event) => event.stopPropagation()}
              className='shrink-0'
            >
              <ItemIcon id={item.id} className='w-6 h-6' />
            </a>
            <div className='grow min-w-0'>
              <div className={cn('truncate', `text-quality-${item.quality}`)}>
                {item.name}
              </div>
              <div className='text-xs text-muted-foreground truncate'>
                {item.encounterName}
              </div>
            </div>
            <Check
              size={16}
              className={cn(
                'ml-auto min-w-4',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          </div>
        ),
      })),
    [lookup, gameVersion],
  )

  const filterValue =
    trinketFilterId === undefined
      ? undefined
      : filterOptions.find((option) =>
          option.value.startsWith(`${trinketFilterId}::`),
        )?.value

  const columns: ColumnDef<TrinketMemberRow>[] = useMemo(
    () => [
      {
        ...genericSorting<TrinketMemberRow>('text', 'Character'),
        id: 'name',
        size: 175,
        accessorFn: (row) => row.member.characterReference.name,
        cell: (info) => (
          <div className='flex'>
            <TeamMemberLink member={info.row.original.member} iconType='both' />
          </div>
        ),
      },
      {
        id: 'equipped',
        header: 'Equipped',
        size: 140,
        enableSorting: false,
        cell: (info) => {
          const equipped = info.row.original.equippedTrinkets.filter(
            (item): item is EquippedItem => !!item?.id,
          )

          if (equipped.length === 0) {
            return <span className='text-muted-foreground'>&mdash;</span>
          }

          return (
            <div className='flex items-center gap-3'>
              {equipped.map((item, index) => (
                <a
                  key={index}
                  href={wowheadUrl(gameVersion, item)}
                  target='_blank'
                  rel='noreferrer'
                  className={cn(
                    'inline-flex items-center gap-1.5 font-semibold whitespace-nowrap',
                    itemQualityClasses[item.quality || 0],
                  )}
                >
                  <ItemIcon id={item.id} className='w-5 h-5' />
                  <span>{item.ilvl}</span>
                </a>
              ))}
            </div>
          )
        },
      },
      ...trinketRanks.map(
        (rank): ColumnDef<TrinketMemberRow> => ({
          id: rank,
          header: trinketRankColumnLabels[rank],
          size: 180,
          enableSorting: false,
          cell: (info) => {
            const row = info.row.original
            return (
              <TrinketStatusCell
                row={row}
                rank={rank}
                lookup={lookup}
                minimumTrack={minimumTrack}
                canEdit={canEditCharacter(row.member.characterReference)}
                onChange={(choice) =>
                  setRankChoice(
                    row.member.characterReference.id,
                    row.storedPriority,
                    rank,
                    choice,
                  )
                }
              />
            )
          },
        }),
      ),
    ],
    [
      accessLevel,
      user,
      team.settings.wishlistLocked.value,
      lookup,
      minimumTrack,
      gameVersion,
      setRankChoice,
    ],
  )

  if (!team.settings.trinketPriorityEnabled.value) {
    return null
  }

  return (
    <div>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>
          Trinket status
        </div>
        {seasons.length > 1 && (
          <SeasonSelect
            className='w-52'
            value={selectedSeason.id.toString()}
            setValue={(value) => {
              const newSeason = seasons.find(
                (season) => season.id.toString() === value,
              ) as Season
              setSelectedSeason(newSeason)
            }}
          />
        )}
      </h2>

      {visibleMembers.length === 0 ? (
        <NoMemberWarning className='my-6' section='loot' />
      ) : isLookupPending ? (
        <Skeleton className='h-160 mt-2' />
      ) : Object.keys(lookup).length === 0 ? (
        <Card className='p-5 mt-4'>
          <p className='text-center text-muted-foreground py-12'>
            This page will be available for this season soon
          </p>
        </Card>
      ) : (
        <div className='mt-4 flex flex-col lg:flex-row gap-4'>
          <TrinketPrioritiesSummary
            rows={rows}
            lookup={lookup}
            minimumTrack={minimumTrack}
            selectedTrinketId={trinketFilterId}
            onSelectTrinket={(id) =>
              setTrinketFilterId((current) => (current === id ? undefined : id))
            }
          />

          <div className='min-w-0 flex-1 space-y-2'>
            <Select
              value={filterValue}
              setValue={(next) => {
                if (!next) {
                  setTrinketFilterId(undefined)
                  return
                }
                setTrinketFilterId(Number(next.split('::')[0]))
              }}
              options={filterOptions}
              isSearchable
              isClearable={trinketFilterId !== undefined}
              placeholder='All trinkets'
              emptyMessage='No trinkets available'
              className='w-72'
            />

            <DataTable
              columns={columns}
              data={filteredRows}
              includeHeader={true}
              getRowId={(row) => row.member.id.toString()}
              sortingState={[{ id: 'name', desc: false }]}
              emptyMessage={() =>
                trinketFilterId !== undefined
                  ? 'No characters selected this trinket'
                  : 'No characters'
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TrinketPrioritiesOverview
